// Synthesis and annexes of a report (D457–D460), after the owner's benchmark report: chapter 11's mechanisms × cases
// matrix, annex A's leads, annex B's per-entry coverage, and the chain-link classification of actors. Each is a
// section of the report's own copy of its sections (`kind: 'matrix' | 'leads' | 'coverage'`), printed as a table
// built here; the leads are the analyst's rows (`report_leads`), imported from the case files' leads lists or typed;
// the matrix and the coverage are read from the records when shown or exported; the matrix's "what it suggests" is
// the analyst's note per mechanism (`report_mechanisms`).

import { caseFileParts } from './case-file';
import type { DossierTable } from './dossier';
import { markerRefs, stripMarkers } from './findings';
import { RELIABILITY_LABELS, type ReliabilityStatus, strongestReliability } from './reliability';
import type { ReportLanguage } from './report-language';
import { isCaseSection, type ReportTemplateSection } from './reports';

export const SYNTHESIS_KINDS = ['matrix', 'leads', 'coverage'] as const;
export type SynthesisKind = (typeof SYNTHESIS_KINDS)[number];
export const isSynthesisKind = (v: unknown): v is SynthesisKind => SYNTHESIS_KINDS.includes(v as SynthesisKind);
export const isSynthesisSection = (s: Partial<ReportTemplateSection> | null | undefined): boolean => isSynthesisKind(s?.kind);

/** The field (and list) that classifies an actor's link in the chain of influence (D460). */
export const CHAIN_LINK_FIELD = 'chain_link';

export const SYNTHESIS_LIMITS = {
  leadsPerReport: 200,
  leadText: 2000,
  leadClaims: 50,
  mechanismNote: 2000,
  /** Actors of the report's query the coverage annex lists. */
  coverageRows: 300,
} as const;

// ─── Words ───────────────────────────────────────────────────────────────────

export const LEAD_STATUSES = ['open', 'in_progress', 'followed', 'dropped'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];
export const isLeadStatus = (v: unknown): v is LeadStatus => LEAD_STATUSES.includes(v as LeadStatus);

export interface SynthesisLabels {
  sectionTitles: Record<SynthesisKind, string>;
  matrixTitle: string;
  matrixHeaders: [string, string, string, string];
  noMatrix: string;
  leadsTitle: string;
  leadsHeaders: [string, string, string, string, string];
  leadStatus: Record<LeadStatus, string>;
  noLeads: string;
  coverageTitle: string;
  coverageHeaders: [string, string, string, string];
  noCoverage: string;
  /** "3 facts: 1 official document, 2 single source". */
  established: (counts: Record<ReliabilityStatus, number>) => string;
  nothingEstablished: string;
  caseFile: string;
  notSet: string;
}

const establishedWords = (lang: ReportLanguage) => (counts: Record<ReliabilityStatus, number>) => {
  const total = Object.values(counts).reduce((n, x) => n + x, 0);
  const parts = (['official_document', 'corroborated', 'single_source', 'hypothesis'] as const)
    .filter(k => counts[k] > 0)
    .map(k => `${counts[k]} ${RELIABILITY_LABELS[lang][k].toLowerCase()}`);
  const head = lang === 'fr' ? `${total} ${total === 1 ? 'fait' : 'faits'}` : `${total} ${total === 1 ? 'fact' : 'facts'}`;
  return parts.length ? `${head}: ${parts.join(', ')}` : head;
};

export const SYNTHESIS_LABELS: Record<ReportLanguage, SynthesisLabels> = {
  en: {
    sectionTitles: { matrix: 'Mechanisms and cases', leads: 'Annex: leads', coverage: 'Annex: coverage by entry' },
    matrixTitle: 'Mechanisms × cases',
    matrixHeaders: ['Mechanism', 'Cases where seen', 'What it suggests', 'Status of the facts'],
    noMatrix: 'No mechanism recorded on the cases yet: add modes of influence to the actors of the case files.',
    leadsTitle: 'Leads',
    leadsHeaders: ['#', 'Lead', 'What is known', 'Status', 'How to follow it'],
    leadStatus: { open: 'Open', in_progress: 'In progress', followed: 'Followed', dropped: 'Dropped' },
    noLeads: 'No lead yet: import the case files’ leads, or add one.',
    coverageTitle: 'Coverage by entry',
    coverageHeaders: ['Actor', 'Chain link', 'What the report establishes', 'Sections'],
    noCoverage: 'The report’s question lists no actor.',
    established: establishedWords('en'),
    nothingEstablished: 'Nothing cited',
    caseFile: 'case file',
    notSet: 'Not set',
  },
  fr: {
    sectionTitles: { matrix: 'Mécanismes et cas', leads: 'Annexe : pistes', coverage: 'Annexe : couverture par entrée' },
    matrixTitle: 'Mécanismes × cas',
    matrixHeaders: ['Mécanisme', 'Cas où il apparaît', 'Ce qu’il suggère', 'Statut des faits'],
    noMatrix: 'Aucun mécanisme relevé sur les cas pour l’instant : ajoutez des modes d’influence aux acteurs des études de cas.',
    leadsTitle: 'Pistes',
    leadsHeaders: ['N°', 'Piste', 'Ce que l’on sait', 'Statut', 'Comment la suivre'],
    leadStatus: { open: 'Ouverte', in_progress: 'En cours', followed: 'Suivie', dropped: 'Abandonnée' },
    noLeads: 'Aucune piste pour l’instant : importez les pistes des études de cas, ou ajoutez-en une.',
    coverageTitle: 'Couverture par entrée',
    coverageHeaders: ['Acteur', 'Maillon', 'Ce que le rapport établit', 'Sections'],
    noCoverage: 'La question du rapport ne liste aucun acteur.',
    established: establishedWords('fr'),
    nothingEstablished: 'Rien de cité',
    caseFile: 'étude de cas',
    notSet: 'Non renseigné',
  },
};

export const synthesisLabels = (lang: ReportLanguage = 'en'): SynthesisLabels => SYNTHESIS_LABELS[lang];

/** The guidance of each synthesis section (its text is the analyst's; the table is built from the records). */
export const SYNTHESIS_GUIDANCE: Record<SynthesisKind, string> = {
  matrix:
    'Written by the analyst: what the mechanisms seen across the case files add up to, and which cases show the same mechanism. The table below is built from the modes of influence recorded on the cases.',
  leads:
    'Written by the analyst: how the leads below were chosen and in what order to follow them. The table lists each lead with what is known and how to follow it.',
  coverage:
    'Written by the analyst, if anything: what the coverage table shows about the report’s scope. The table lists every actor of the report’s question with what the report establishes about it and where.',
};

/** A synthesis section, titled in the report's language. */
export const synthesisSection = (kind: SynthesisKind, lang: ReportLanguage = 'en'): ReportTemplateSection => ({
  key: kind,
  title: synthesisLabels(lang).sectionTitles[kind],
  guidance: SYNTHESIS_GUIDANCE[kind],
  starter: null,
  kind,
});

/**
 * The sections with a synthesis section added: the matrix before "recommendations" (after the scorecard), the two
 * annexes last (leads before coverage). Left as it is when already there.
 */
export function insertSynthesisSection(
  sections: readonly ReportTemplateSection[],
  section: ReportTemplateSection,
): ReportTemplateSection[] {
  if (sections.some(s => s.key === section.key)) return [...sections];
  const out = [...sections];
  if (section.kind === 'matrix') {
    const at = out.findIndex(s => s.key === 'recommendations');
    out.splice(at < 0 ? out.length : at, 0, section);
    return out;
  }
  if (section.kind === 'leads') {
    const cov = out.findIndex(s => s.kind === 'coverage');
    out.splice(cov < 0 ? out.length : cov, 0, section);
    return out;
  }
  out.push(section);
  return out;
}

// ─── Leads (annex A) ─────────────────────────────────────────────────────────

export interface Lead {
  id: string;
  reportId: string;
  position: number;
  /** The lead itself: what to check. */
  what: string;
  /** What is known so far (words; the claims are `claimIds`). */
  known: string;
  status: LeadStatus;
  /** How to follow it: the register, filing, person or record. */
  how: string;
  claimIds: string[];
  /** The case file it came from, or null. */
  sectionKey: string | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export type LeadInput = Pick<Lead, 'what' | 'known' | 'status' | 'how' | 'claimIds' | 'sectionKey'>;

const CLAIM_ID = /^[0-9A-Za-z_-]{1,64}$/;
const KEY = /^[a-z][a-z0-9_]{0,47}$/;

/** A lead as posted; `partial` keeps absent fields absent. */
export function parseLeadInput(
  raw: unknown,
  opts: { partial?: boolean } = {},
): { ok: true; value: Partial<LeadInput> } | { ok: false; error: string } {
  const b = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out: Partial<LeadInput> = {};
  for (const [key, label, required] of [
    ['what', 'The lead', true],
    ['known', 'What is known', false],
    ['how', 'How to follow it', false],
  ] as const) {
    if (b[key] === undefined || b[key] === null) {
      if (!opts.partial) {
        if (required) return { ok: false, error: 'Say what the lead is.' };
        out[key] = '';
      }
      continue;
    }
    if (typeof b[key] !== 'string') return { ok: false, error: `${label} must be text.` };
    const v = (b[key] as string).replace(/\r\n?/g, '\n').trim();
    if (required && !v) return { ok: false, error: 'Say what the lead is.' };
    if (v.length > SYNTHESIS_LIMITS.leadText)
      return { ok: false, error: `Keep ${label.toLowerCase()} under ${SYNTHESIS_LIMITS.leadText.toLocaleString('en')} characters.` };
    out[key] = v;
  }
  if (b.status !== undefined) {
    if (!isLeadStatus(b.status)) return { ok: false, error: 'The status is open, in progress, followed or dropped.' };
    out.status = b.status;
  } else if (!opts.partial) out.status = 'open';
  if (b.claimIds !== undefined) {
    if (!Array.isArray(b.claimIds) || !b.claimIds.every(x => typeof x === 'string' && CLAIM_ID.test(x)))
      return { ok: false, error: 'The claims must be a list of claim ids.' };
    const ids = [...new Set(b.claimIds as string[])];
    if (ids.length > SYNTHESIS_LIMITS.leadClaims)
      return { ok: false, error: `Link at most ${SYNTHESIS_LIMITS.leadClaims} claims to one lead.` };
    out.claimIds = ids;
  } else if (!opts.partial) out.claimIds = [];
  if (b.sectionKey !== undefined) {
    if (b.sectionKey !== null && !(typeof b.sectionKey === 'string' && KEY.test(b.sectionKey)))
      return { ok: false, error: 'The section must be one of the report’s.' };
    out.sectionKey = b.sectionKey as string | null;
  } else if (!opts.partial) out.sectionKey = null;
  return { ok: true, value: out };
}

/** A lead as a case file's draft wrote it: the bullet's text without its markers, and the refs it cites. */
export interface CaseLead {
  sectionKey: string;
  text: string;
  refs: string[];
}

/** The leads of every case file section's text (the fixed "Leads" part), with the refs each cites. */
export function caseLeadsOf(sections: readonly ReportTemplateSection[], bodies: ReadonlyMap<string, string>): CaseLead[] {
  const out: CaseLead[] = [];
  for (const s of sections) {
    if (!isCaseSection(s)) continue;
    const parts = caseFileParts(bodies.get(s.key) ?? '');
    for (const lead of parts?.leads ?? []) {
      const text = stripMarkers(lead);
      if (text) out.push({ sectionKey: s.key, text, refs: markerRefs(lead) });
    }
  }
  return out;
}

/** The leads table of the annex. */
export function leadsTable(
  list: readonly Lead[],
  statusOf: (lead: Lead) => ReliabilityStatus | null,
  lang: ReportLanguage = 'en',
): DossierTable {
  const l = synthesisLabels(lang);
  return {
    title: l.leadsTitle,
    headers: [...l.leadsHeaders],
    rows: list.map((x, i) => {
      const st = statusOf(x);
      const known = [x.known, st ? `(${RELIABILITY_LABELS[lang][st]})` : ''].filter(Boolean).join(' ');
      return [String(i + 1), x.what, known, l.leadStatus[x.status], x.how];
    }),
    total: list.length,
    note: null,
  };
}

// ─── Mechanisms × cases (chapter 11) ─────────────────────────────────────────

/** One mechanism's row: the cases where it was seen and the status of the facts behind them. */
export interface MatrixRow {
  /** The mechanism: a value key of the influence-category list. */
  key: string;
  label: string;
  cases: { sectionKey: string; title: string; entityId: string; methods: string[]; status: ReliabilityStatus | null }[];
  /** The analyst's "what it suggests". */
  note: string;
  status: ReliabilityStatus | null;
}

/** The rows of the matrix: one per mechanism seen on at least one case, in the list's order, then the unseen ones with a note. */
export function matrixRows(
  mechanisms: readonly { key: string; label: string }[],
  seen: ReadonlyMap<string, MatrixRow['cases']>,
  notes: ReadonlyMap<string, string>,
): MatrixRow[] {
  return mechanisms
    .map(m => {
      const cases = seen.get(m.key) ?? [];
      return { key: m.key, label: m.label, cases, note: notes.get(m.key) ?? '', status: strongestReliability(cases.map(c => c.status)) };
    })
    .filter(r => r.cases.length || r.note.trim());
}

export function matrixTable(rows: readonly MatrixRow[], lang: ReportLanguage = 'en'): DossierTable {
  const l = synthesisLabels(lang);
  return {
    title: l.matrixTitle,
    headers: [...l.matrixHeaders],
    rows: rows.map(r => [
      r.label,
      r.cases.map(c => (c.methods.length ? `${c.title} (${c.methods.join('; ')})` : c.title)).join('; '),
      r.note,
      r.status ? RELIABILITY_LABELS[lang][r.status] : '',
    ]),
    total: rows.length,
    note: null,
  };
}

// ─── Coverage by entry (annex B) ─────────────────────────────────────────────

export interface CoverageRow {
  entityId: string;
  name: string;
  chainLink: string | null;
  /** Cited claims about the actor, by reliability status. */
  counts: Record<ReliabilityStatus, number>;
  /** The sections whose text cites a claim about the actor, in document order. */
  sections: { key: string; title: string }[];
  caseFile: boolean;
}

export const emptyCounts = (): Record<ReliabilityStatus, number> => ({
  official_document: 0,
  corroborated: 0,
  single_source: 0,
  hypothesis: 0,
});

export function coverageTable(rows: readonly CoverageRow[], lang: ReportLanguage = 'en'): DossierTable {
  const l = synthesisLabels(lang);
  return {
    title: l.coverageTitle,
    headers: [...l.coverageHeaders],
    rows: rows.map(r => {
      const total = Object.values(r.counts).reduce((n, x) => n + x, 0);
      return [
        r.caseFile ? `${r.name} (${l.caseFile})` : r.name,
        r.chainLink ?? l.notSet,
        total ? l.established(r.counts) : l.nothingEstablished,
        r.sections.map(s => s.title).join('; '),
      ];
    }),
    total: rows.length,
    note: null,
  };
}
